import {
  AppBarSaveButton,
  AppBarValidationIndicator,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import {
  modelToJsonPatchDocument,
  getLookupTracker,
  buildMetaData,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Prompt } from "react-router-dom";
import {
  EventNotification,
  EventNotificationStatus,
  toApiModel,
} from "../../domain/EventNotification";
import { updatingItem } from "../../redux/actions";
import { ItemState } from "../../redux/state";
import { ModuleAppBar } from "../ModuleAppBar";
import { SectionProps } from "../sections/SectionProps";
import { ItemDetails } from "./ItemDetails";
import { ItemDetailsOfqual } from "./ItemDetailsOfqual";
import { ItemTabs } from "./ItemTabs";

import { ShareButtonAndDialog } from "./ShareButtonAndDialog";
import { UserType } from "../../redux/types";
import { CloseButtonAndDialog } from "./CloseButtonAndDialog";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { UserRole } from "../../domain/userRole";
import { ReopenButtonAndDialog } from "./ReopenButtonAndDialog";
import { authSelector } from "../../redux/selectors";

type NonNullableItemState = ItemState & {
  item: Exclude<ItemState["item"], null>;
};

export const ItemForm: React.FC<{
  state: NonNullableItemState;
}> = ({ state: { item, status, lastCrudResult } }) => {
  const {
    handleSubmit,
    control,
    formState,
    errors,
    trigger,
    setValue,
    setError,
    clearErrors,
    getValues,
    reset,
    watch
  } = useForm<EventNotification>({
    defaultValues: item,
    shouldUnregister: false,
  });

  useEffect(() => {
    // on save (detected by a new item in redux) we need to clear dirtyFields, so reset is required
    reset(item);
  }, [reset, item]);

  const dispatch = useDispatch();
  const { isInRole, userType, token } = useAuth(authSelector);

  const canCloseCase = !!(
    isInRole(UserRole.ITAdmin, UserRole.OfqualEventNotificationsAdmin) ||
    (isInRole(UserRole.CCEAEventNotificationsAdmin) &&
      !item?.regulatorsAffectedOfqual &&
      item?.regulatorsAffectedCcea)
  );

  const isClosed = item.status === EventNotificationStatus.Closed;

  const isMagiclinkUser = userType === UserType.Magic;
  const disabled =
    status === "UPDATING" ||
    item.status === EventNotificationStatus.Closed ||
    isMagiclinkUser;

  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  const { dirtyFields } = formState;
  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const onSubmit = (newModel: EventNotification) => {
    const fields = Object.keys(dirtyFields) as (keyof EventNotification)[];
    let document = modelToJsonPatchDocument(toApiModel(newModel), fields);
    const resourceTagPatch = document.find(
      (patch) => patch.path === "/resourceTags"
    );
    let resourceTagIds: number[] = item.resourceTags;
    if (resourceTagPatch) {
      resourceTagIds = resourceTagPatch.value;
      document = document.filter((x) => x !== resourceTagPatch);
    }
    const meta = buildMetaData(newModel, item, fields, lookupTracker);

    dispatch(updatingItem(item.id, { document, meta }, resourceTagIds));
  };

  const sectionProps: SectionProps<EventNotification> = {
    isCreateMode: false,
    user: { userType, token },
    disabled,
    control,
    errors,
    trigger,
    watch,
    formState,
    setValue: (name, value) => setValue(name, value),
    getValues,
    registerCustomError: (name: string, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
    lookupTracker,
  };

  const fieldsToValidate: (keyof EventNotification)[] = [
    "inherentRiskImpact",
    "inherentRiskLikelihood",
    "residualRiskImpact",
    "residualRiskLikelihood",
    "riskCategory",
    "caseSummary",
    "eventType",
    "eventSubType",
    "ofqualEventManagementActivities",
    "typeOfQualVtq",
    "typeOfQualGq",
    "directorate",
    "rootCause",
    "rootCauseDetails",
    "eventNotificationOfqualMitigationTaken",
    "eventNotificationOfqualActionTaken",
    "ofqualActionTakenRationale",
    "adverseEffectOutcome",
    "agreeWithAodeclaration",
    "aoengagement",
    "malpracticeAllegation",
    "finalDetermination",
  ];

  const shouldQualErrorsBeIgnored = (field: string): boolean => {
    const vtq = getValues("typeOfQualVtq");
    const gq = getValues("typeOfQualGq");
    return (
      (field === "typeOfQualVtq" && !!gq) || (field === "typeOfQualGq" && !!vtq)
    );
  };

  const onCloseValidate = (): boolean => {
    let isValid = true;
    fieldsToValidate.forEach((field) => {
      const value = getValues(field);
      if (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ) {
        if (shouldQualErrorsBeIgnored(field)) return;

        const isCurrentlyARegisteredError = Object.keys(errors).includes(field);
        if (!isCurrentlyARegisteredError) {
          setError(field, {
            type: "required",
          });
        }
        isValid = false;
      }
    });
    return Object.keys(errors).length === 0 && isValid;
  };

  return (
    <>
      <Prompt
        message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
        when={Object.keys(formState.dirtyFields).length > 0}
      />
      <ModuleAppBar breadcrumbs={[{ label: `${item.title}` }]}>
        <AppBarValidationIndicator errors={errors} />

        {!isMagiclinkUser ? (
          <>
            {canCloseCase ? (
              onCloseValidate() ? (
                <CloseButtonAndDialog eventNotification={item} />
              ) : null
            ) : null}

            {canCloseCase && isClosed ? (
              <ReopenButtonAndDialog eventNotification={item} />
            ) : null}
            <ShareButtonAndDialog id={item.id} />
            <AppBarSaveButton
              onClick={handleSubmit(onSubmit)}
              isSubmitting={status === "UPDATING"}
              isSubmitted={lastCrudResult.isSuccess}
              dataTest="save-en-button"
              disabled={disabled}
            />
          </>
        ) : null}
      </ModuleAppBar>
      <ItemTabs indicatorProps={sectionProps}>
        {(activeTab) =>
          activeTab === "ofqual" ? (
            <ItemDetailsOfqual sectionProps={sectionProps} />
          ) : (
            <ItemDetails sectionProps={sectionProps} />
          )
        }
      </ItemTabs>
    </>
  );
};
